'use client'

import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react'

interface ModeContextType {
  // Theme mode
  themeMode: 'light' | 'dark' | 'system'
  setThemeMode: (mode: 'light' | 'dark' | 'system') => void
  
  // App mode (Jewelry/Pawn)
  appMode: 'JEWELRY' | 'PAWN'
  setAppMode: (mode: 'JEWELRY' | 'PAWN') => void
  isPawnMode: boolean
  
  isInitialized: boolean
}

const ModeContext = createContext<ModeContextType | undefined>(undefined)

export const useMode = () => {
  const context = useContext(ModeContext)
  
  if (context === undefined) {
    // Return a safe default instead of throwing an error
    return {
      themeMode: 'system' as const,
      setThemeMode: () => {
        console.warn('Mode provider not ready - setThemeMode called before initialization');
      },
      appMode: 'JEWELRY' as const,
      setAppMode: () => {
        console.warn('Mode provider not ready - setAppMode called before initialization');
      },
      isPawnMode: false,
      isInitialized: true
    }
  }
  return context
}

export const ModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<'light' | 'dark' | 'system'>('dark')
  const [appMode, setAppModeState] = useState<'JEWELRY' | 'PAWN'>('JEWELRY')
  const [mounted, setMounted] = useState(false)
  const [providerReady, setProviderReady] = useState(false)

  // Ensure we only run client-side code after mounting
  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    // Only run in browser environment and after mounting
    if (typeof window === 'undefined' || !mounted) {
      setProviderReady(true)
      return
    }

    // Load theme mode from localStorage
    const savedThemeMode = localStorage.getItem('themeMode') as 'light' | 'dark' | 'system' | null
    if (savedThemeMode) {
      setThemeModeState(savedThemeMode)
    }
    
    // Load app mode from localStorage
    const savedAppMode = localStorage.getItem('appMode') as 'JEWELRY' | 'PAWN' | null
    if (savedAppMode) {
      setAppModeState(savedAppMode)
    }
    
    setProviderReady(true)
  }, [mounted])

  const setThemeMode = useCallback((newMode: 'light' | 'dark' | 'system') => {
    if (!providerReady) {
      throw new Error('Mode provider not ready')
    }
    
    setThemeModeState(newMode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('themeMode', newMode)
    }
  }, [providerReady])

  const setAppMode = useCallback((newMode: 'JEWELRY' | 'PAWN') => {
    if (!providerReady) {
      throw new Error('Mode provider not ready')
    }
    
    setAppModeState(newMode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('appMode', newMode)
    }
  }, [providerReady])

  // Compute isPawnMode for convenience
  const isPawnMode = appMode === 'PAWN'

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    themeMode,
    setThemeMode,
    appMode,
    setAppMode,
    isPawnMode,
    isInitialized: providerReady
  }), [themeMode, setThemeMode, appMode, setAppMode, isPawnMode, providerReady])

  // Always render children to prevent hydration mismatch
  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  )

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  )
} 